import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";

export default function NewsSection({
                                        primaryColor,
                                        secondaryColor,
                                        styles,
                                        news,
                                        isLoading = false,
                                        onNewsClick,
                                    }) {
    const [selectedNews, setSelectedNews] = useState(null);

    const visibleNews = Array.isArray(news) ? news : [];

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setSelectedNews(null);
            }
        };

        if (selectedNews) {
            document.body.style.overflow = "hidden";
            window.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [selectedNews]);

    const openModal = (item) => {
        setSelectedNews(item);
        onNewsClick?.(item);
    };

    const closeModal = () => {
        setSelectedNews(null);
    };

    return (
        <section style={styles.section}>
            <motion.h2
                style={{ ...styles.sectionTitle, color: primaryColor }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
            >
                Neuigkeiten
            </motion.h2>

            <motion.p
                style={{
                    maxWidth: "760px",
                    margin: "0 auto 3rem",
                    textAlign: "center",
                    fontSize: "clamp(1rem, 2.5vw, 1.1rem)",
                    lineHeight: "1.8",
                    color: "#555",
                    padding: "0 1rem",
                }}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                viewport={{ once: true }}
            >
                Hier finden Sie aktuelle Informationen zu unserer Havaneser-Zucht,
                geplanten Würfen und besonderen Momenten mit unseren Hunden.
            </motion.p>

            {isLoading ? (
                <p
                    style={{
                        textAlign: "center",
                        color: "#555",
                        fontSize: "1rem",
                    }}
                >
                    Neuigkeiten werden geladen...
                </p>
            ) : visibleNews.length === 0 ? (
                <div
                    style={{
                        maxWidth: "720px",
                        margin: "0 auto",
                        textAlign: "center",
                        backgroundColor: "#fff",
                        borderRadius: "22px",
                        padding: "clamp(1.25rem, 4vw, 2rem)",
                        boxShadow: "0 10px 28px rgba(0, 0, 0, 0.06)",
                        border: `1px solid ${primaryColor}18`,
                    }}
                >
                    <p
                        style={{
                            margin: 0,
                            color: "#555",
                            fontSize: "1rem",
                            lineHeight: 1.7,
                        }}
                    >
                        Aktuell gibt es keine Neuigkeiten.
                    </p>
                </div>
            ) : (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
                        gap: "1.5rem",
                        alignItems: "stretch",
                        width: "100%",
                    }}
                >
                    {visibleNews.map((item, index) => (
                        <motion.article
                            key={item.id}
                            style={{
                                backgroundColor: "#fff",
                                borderRadius: "24px",
                                boxShadow: "0 12px 35px rgba(0, 0, 0, 0.08)",
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                padding: "clamp(1.25rem, 4vw, 1.9rem)",
                                border: `1px solid ${primaryColor}18`,
                            }}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: index * 0.15 }}
                            viewport={{ once: true }}
                            whileHover={{
                                y: -6,
                                boxShadow: "0 18px 45px rgba(0, 0, 0, 0.12)",
                            }}
                        >
                            <span
                                style={{
                                    display: "inline-block",
                                    alignSelf: "flex-start",
                                    marginBottom: "1rem",
                                    padding: "0.35rem 0.8rem",
                                    borderRadius: "999px",
                                    backgroundColor: `${primaryColor}18`,
                                    color: primaryColor,
                                    fontSize: "0.85rem",
                                    fontWeight: 600,
                                }}
                            >
                                {item.date}
                            </span>

                            <h3
                                style={{
                                    margin: "0 0 1rem",
                                    fontSize: "clamp(1.2rem, 3vw, 1.4rem)",
                                    lineHeight: "1.35",
                                    color: primaryColor,
                                }}
                            >
                                {item.title}
                            </h3>

                            <p
                                style={{
                                    margin: "0 0 1.5rem",
                                    color: "#555",
                                    lineHeight: "1.75",
                                    flexGrow: 1,
                                    fontSize: "1rem",
                                }}
                            >
                                {item.text}
                            </p>

                            <motion.button
                                type="button"
                                style={{
                                    ...styles.button,
                                    backgroundColor: primaryColor,
                                    alignSelf: "flex-start",
                                    marginTop: "auto",
                                }}
                                whileHover={{
                                    scale: 1.05,
                                    backgroundColor: secondaryColor,
                                }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => openModal(item)}
                            >
                                {item.buttonLabel || "Mehr lesen"}
                            </motion.button>
                        </motion.article>
                    ))}
                </div>
            )}

            <AnimatePresence>
                {selectedNews && (
                    <motion.div
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 9999,
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "1rem",
                        }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeModal}
                    >
                        <motion.div
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="news-modal-title"
                            style={{
                                width: "min(100%, 680px)",
                                maxHeight: "90vh",
                                overflowY: "auto",
                                backgroundColor: "#fff",
                                borderRadius: "24px",
                                padding: "clamp(1.5rem, 5vw, 2.5rem)",
                                boxShadow: "0 24px 70px rgba(0, 0, 0, 0.25)",
                                position: "relative",
                            }}
                            initial={{ opacity: 0, y: 25, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 25, scale: 0.96 }}
                            transition={{ duration: 0.25 }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <button
                                type="button"
                                aria-label="Neuigkeit schließen"
                                onClick={closeModal}
                                style={{
                                    position: "absolute",
                                    top: "1rem",
                                    right: "1rem",
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "50%",
                                    border: "none",
                                    backgroundColor: `${primaryColor}14`,
                                    color: primaryColor,
                                    fontSize: "1.5rem",
                                    lineHeight: 1,
                                    cursor: "pointer",
                                }}
                            >
                                ×
                            </button>

                            <span
                                style={{
                                    display: "inline-block",
                                    marginBottom: "1rem",
                                    padding: "0.35rem 0.8rem",
                                    borderRadius: "999px",
                                    backgroundColor: `${primaryColor}18`,
                                    color: primaryColor,
                                    fontSize: "0.85rem",
                                    fontWeight: 600,
                                }}
                            >
                                {selectedNews.date}
                            </span>

                            <h3
                                id="news-modal-title"
                                style={{
                                    margin: "0 2.5rem 1.25rem 0",
                                    color: primaryColor,
                                    fontSize: "clamp(1.5rem, 5vw, 2rem)",
                                    lineHeight: "1.25",
                                }}
                            >
                                {selectedNews.title}
                            </h3>

                            <p
                                style={{
                                    margin: 0,
                                    color: "#555",
                                    lineHeight: "1.85",
                                    fontSize: "clamp(1rem, 2.5vw, 1.08rem)",
                                    whiteSpace: "pre-line",
                                }}
                            >
                                {selectedNews.content || selectedNews.text}
                            </p>

                            <motion.button
                                type="button"
                                style={{
                                    ...styles.button,
                                    backgroundColor: primaryColor,
                                    marginTop: "2rem",
                                }}
                                whileHover={{
                                    scale: 1.04,
                                    backgroundColor: secondaryColor,
                                }}
                                whileTap={{ scale: 0.95 }}
                                onClick={closeModal}
                            >
                                Schließen
                            </motion.button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}